import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Renders a full-viewport fallback instead of an inline card. */
  fullscreen?: boolean;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught render error:', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    const { children, fullscreen } = this.props;

    if (!error) return children;

    const message = error.message || 'An unexpected error occurred.';

    if (fullscreen) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-surface-0 px-4 text-center">
          <div className="text-base font-semibold text-ink">Something went wrong</div>
          <div className="max-w-sm text-sm text-ink-muted">{message}</div>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => window.location.reload()}
          >
            Reload page
          </button>
        </div>
      );
    }

    return (
      <div role="alert" className="card flex flex-col items-center gap-3 px-6 py-10 text-center">
        <div className="text-sm font-medium text-ink">Something went wrong</div>
        <div className="max-w-sm text-xs text-ink-muted">{message}</div>
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={() => window.location.reload()}
        >
          Reload page
        </button>
      </div>
    );
  }
}
